import type { ReactElement } from 'react'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import loadable from '@loadable/component'

import type { BackgroundColorSettings, BackgroundWidthSettings } from './Block'
import { defaultBackgroundColorSettings, getDefaultBackgroundWidthSettings } from './Block'
import { themeGroupDelimiter } from '../../../../utils/themes'
import { useColorOptions } from '../../../utils/hooks/colorSettings'
import Button from '../../formComponents/Button'
import ButtonRow from '../../formComponents/ButtonRow'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ './SettingsLayer'))
const ColorField = loadable(() => import(/* webpackChunkName: "editor" */ '../../formComponents/ColorField'), {
  resolveComponent: (components) => components.ColorFieldRaw,
})

type Props = {
  currentColorSettings: BackgroundColorSettings
  currentWidthSettings: BackgroundWidthSettings
  onColorSettingsChange: (settings: BackgroundColorSettings) => void
  onWidthSettingsChange: (settings: BackgroundWidthSettings) => void
  onCancel: () => void
  onSave: () => void
  themeName: string
} & TranslateProps

export default function BlockBackgroundSettings({
  t,
  currentColorSettings,
  currentWidthSettings,
  onColorSettingsChange,
  onWidthSettingsChange,
  onSave,
  onCancel,
  themeName,
}: Readonly<Props>): ReactElement {
  const [showBlockBackgroundSettings, setShowBlockBackgroundSettings] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const {
    backgroundColor,
    colorOrGradient,
    gradientEndColor,
    gradientMidColor,
    gradientOrientation,
    gradientStartColor,
    showGradientMidColor,
  } = currentColorSettings

  const themeNameForLanguageKeys = useSelector<State, string>((state) =>
    state.getIn(['shop', 'theme', 'name'], '').replace('.', '_'),
  )
  const [themeGroupName] = themeNameForLanguageKeys.split(themeGroupDelimiter)
  const tThemeSettings = (subKey: string) => t(`themes:${themeGroupName}.settings.${subKey}`)
  const tColorOptions = (subKey: string) => t(`components.colorFieldComponent.popover.colorPresets.${subKey}.tooltip`)
  const colorOptions = useColorOptions(tColorOptions, tThemeSettings)

  const title = t('backgroundButton.settingsLayer.title')

  const saveColor = () => {
    setShowBlockBackgroundSettings(false)
    onSave()
  }

  const cancelHandler = () => {
    onCancel()
    setShowBlockBackgroundSettings(false)
  }

  const handleSwitch = (event) => {
    event.preventDefault()
    onColorSettingsChange({
      ...currentColorSettings,
      gradientStartColor: gradientEndColor,
      gradientEndColor: gradientStartColor,
    })
  }

  const toggleColorSettings = () => {
    if (showBlockBackgroundSettings) {
      cancelHandler()
    } else setShowBlockBackgroundSettings(true)
  }

  const handleReset = () => {
    const defaults = getDefaultBackgroundWidthSettings(themeName)
    onColorSettingsChange(defaultBackgroundColorSettings)
    onWidthSettingsChange({
      ...defaults,
      isUserModified: false,
    })
  }

  const handleColorReset = () => {
    // Reset width to theme default when color is reset to transparent
    const defaults = getDefaultBackgroundWidthSettings(themeName)
    onWidthSettingsChange({
      ...defaults,
      isUserModified: false,
    })
  }

  const handleSolidColorSelection = () => {
    const newColorSettings = { ...currentColorSettings, colorOrGradient: 'color' as const }
    onColorSettingsChange(newColorSettings)

    // If switching to solid color and it's transparent, reset width to theme default
    if (backgroundColor === 'transparent' || backgroundColor === defaultBackgroundColorSettings.backgroundColor) {
      const defaults = getDefaultBackgroundWidthSettings(themeName)
      onWidthSettingsChange({
        ...defaults,
        isUserModified: false,
      })
    }
  }

  const handleWidthChange = (width: 'content' | 'full') => {
    onWidthSettingsChange({
      ...currentWidthSettings,
      width,
      isUserModified: true,
    })
  }

  const body = (
    <div className="dali-settingslayer-main-content">
      <div className="dali-settingslayer-element">
        <label htmlFor="color">
          <input type="radio" id="color" checked={colorOrGradient === 'color'} onChange={handleSolidColorSelection} />
          {t('backgroundButton.settingsLayer.backgroundStyleRadioButtons.solid.label')}
        </label>
        <div className="dali-settingslayer-solid-background-row" aria-disabled={colorOrGradient === 'gradient'}>
          <ColorField
            value={backgroundColor}
            side="left"
            className="ep-color-field-box"
            presetColors={colorOptions.toJS()}
            offsetDistance={20}
            resetValue={defaultBackgroundColorSettings.backgroundColor}
            onChange={(backgroundColor = defaultBackgroundColorSettings.backgroundColor) =>
              onColorSettingsChange({ ...currentColorSettings, backgroundColor })
            }
            onReset={handleColorReset}
          />
          <span>{t('backgroundButton.settingsLayer.backgroundStyleRadioButtons.solid.colorPicker.label')}</span>
        </div>
      </div>

      <div className="dali-settingslayer-element dali-settingslayer-gradient-background">
        <label htmlFor="gradient" className="dali-settingslayer-gradient-background-label">
          <input
            type="radio"
            id="gradient"
            data-testid="gradient-radio-button"
            checked={colorOrGradient === 'gradient'}
            onChange={() => onColorSettingsChange({ ...currentColorSettings, colorOrGradient: 'gradient' })}
          />
          {t('backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.label')}
        </label>
        <fieldset
          className="dali-settingslayer-element dali-settingslayer-gradient-background-row"
          disabled={colorOrGradient === 'color'}
        >
          <legend>{t('backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.settings')}</legend>
          <div className="dali-settingslayer-gradient-background-content">
            <fieldset className="dali-settingslayer-gradient-background-number">
              <legend className="sr-only">
                {t(
                  'backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.numberRadioButtons.accessibilityLabel',
                )}
              </legend>
              <span>
                <div className="dali-settingslayer-gradient-background-number-radio-button">
                  <input
                    type="radio"
                    id="twoColors"
                    checked={!showGradientMidColor}
                    onChange={() => onColorSettingsChange({ ...currentColorSettings, showGradientMidColor: false })}
                  />
                  <label htmlFor="twoColors">
                    {t(
                      'backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.numberRadioButtons.twoColors',
                    )}
                  </label>
                </div>
                <div className="dali-settingslayer-gradient-background-number-radio-button">
                  <input
                    type="radio"
                    id="threeColors"
                    data-testid="threeColors"
                    checked={showGradientMidColor}
                    onChange={() => onColorSettingsChange({ ...currentColorSettings, showGradientMidColor: true })}
                  />
                  <label htmlFor="threeColors">
                    {t(
                      'backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.numberRadioButtons.threeColors',
                    )}
                  </label>
                </div>
              </span>
            </fieldset>
            <button className="dali-settingslayer-gradient-background-switch" onClick={handleSwitch}>
              {t('backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.switchColorsButton.label')}
            </button>
            <div
              className="dali-settingslayer-gradient-background-content-bar"
              style={{
                background: showGradientMidColor
                  ? `linear-gradient( 90deg, ${gradientStartColor}, ${gradientMidColor}, ${gradientEndColor})`
                  : `linear-gradient( 90deg, ${gradientStartColor}, ${gradientEndColor})`,
              }}
            ></div>
            <div
              className={`dali-settingslayer-gradient-background-content-colors ${
                showGradientMidColor ? 'dali-settingslayer-gradient-background-content-colors-three' : ''
              }`}
            >
              <ColorField
                value={gradientStartColor}
                className="ep-color-field-box"
                presetColors={colorOptions.toJS()}
                offsetDistance={20}
                side="left"
                align="end"
                data-testid="gradient-start-color"
                resetValue={defaultBackgroundColorSettings.gradientStartColor}
                onChange={(gradientStartColor = defaultBackgroundColorSettings.gradientStartColor) =>
                  onColorSettingsChange({ ...currentColorSettings, gradientStartColor })
                }
              />
              {showGradientMidColor && (
                <ColorField
                  value={gradientMidColor}
                  className="ep-color-field-box"
                  presetColors={colorOptions.toJS()}
                  offsetDistance={20}
                  resetValue={defaultBackgroundColorSettings.gradientMidColor}
                  data-testid="gradient-mid-color"
                  onChange={(gradientMidColor = defaultBackgroundColorSettings.gradientMidColor) =>
                    onColorSettingsChange({ ...currentColorSettings, gradientMidColor })
                  }
                  side="right"
                  align="end"
                />
              )}
              <ColorField
                value={gradientEndColor}
                className="ep-color-field-box"
                presetColors={colorOptions.toJS()}
                offsetDistance={20}
                resetValue={defaultBackgroundColorSettings.gradientEndColor}
                data-testid="gradient-end-color"
                onChange={(gradientEndColor = defaultBackgroundColorSettings.gradientEndColor) =>
                  onColorSettingsChange({ ...currentColorSettings, gradientEndColor })
                }
                side="right"
                align="end"
              />
            </div>
          </div>
          <fieldset className="dali-settingslayer-gradient-background-orientation">
            <legend className="dali-settingslayer-gradient-background-orientation-legend ep-form-row-label">
              {t('backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.orientationRadioButtons.label')}
            </legend>
            <div className="dali-settingslayer-gradient-background-orientation-options">
              <span>
                <input
                  type="radio"
                  id="horizontal"
                  checked={gradientOrientation === '90deg'}
                  onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '90deg' })}
                />
                <label htmlFor="horizontal">
                  {t(
                    'backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.orientationRadioButtons.horizontal',
                  )}
                </label>
              </span>
              <span>
                <input
                  type="radio"
                  id="vertical"
                  checked={gradientOrientation === '180deg'}
                  onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '180deg' })}
                />
                <label htmlFor="vertical">
                  {t(
                    'backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.orientationRadioButtons.vertical',
                  )}
                </label>
              </span>
              <span>
                <input
                  type="radio"
                  id="diagonal"
                  checked={gradientOrientation === '45deg'}
                  onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '45deg' })}
                />
                <label htmlFor="diagonal">
                  {t(
                    'backgroundButton.settingsLayer.backgroundStyleRadioButtons.gradient.orientationRadioButtons.diagonal',
                  )}
                </label>
              </span>
            </div>
          </fieldset>
        </fieldset>
      </div>

      <fieldset disabled={colorOrGradient === 'color' && backgroundColor === 'transparent'}>
        <legend className="ep-form-row-small-margin ep-form-row-label">
          {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.label')}
        </legend>
        <div className="dali-settingslayer-background-width-options">
          <label>
            <input
              type="radio"
              id="content-width"
              checked={currentWidthSettings.width === 'content'}
              onChange={() => handleWidthChange('content')}
            />
            {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.contentWidthRadioButton')}
          </label>
          <label>
            <input
              type="radio"
              id="full-width"
              checked={currentWidthSettings.width === 'full'}
              onChange={() => handleWidthChange('full')}
            />
            {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.fullWidthRadioButton')}
          </label>
        </div>
      </fieldset>
      <button type="button" className="ep-settingslayer-block-background-reset-button" onClick={handleReset}>
        {t('backgroundButton.settingsLayer.resetButton.label')}
      </button>
    </div>
  )
  const footer = (
    <ButtonRow>
      <Button type="button" variant="cancel" onClick={cancelHandler}>
        {t('backgroundButton.settingsLayer.cancelButton.label')}
      </Button>
      <Button type="button" onClick={saveColor}>
        {t('backgroundButton.settingsLayer.saveButton.label')}
      </Button>
    </ButtonRow>
  )

  return (
    <>
      <button
        ref={buttonRef}
        className="dali-block-actionbar-button dali-block-actionbar-button-background-color"
        title={t('backgroundButton.label')}
        onClick={() => toggleColorSettings()}
      />
      <SettingsLayer
        open={showBlockBackgroundSettings}
        className="ep-settingslayer-block-background"
        virtualRef={buttonRef}
        side="right"
        onEscapeKeyDown={cancelHandler}
      >
        {({ renderLayout }) => renderLayout({ title, body, footer })}
      </SettingsLayer>
    </>
  )
}
